import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import { Button } from '@/components/ui/Buttons';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import { useModalManager } from '@/components/common/Modal';
import useStates from '@/hooks/api/v2/useStates';
import useProgramStates from '@/hooks/api/v2/useProgramStates';
import { recordsEqual } from './EditProgramStateGrade.utils';
import StateGradeSelect from './StateGradeSelect';

interface EditProgramStateGradeProps {
  program: {
    id: number;
    title: string;
    grade: string;
  };
}

const EditProgramStateGrade = ({ program }: EditProgramStateGradeProps) => {
  const modalManager = useModalManager();
  const { data: states, isLoading: isStatesLoading } = useStates();
  const { data: programStates, isLoading: isProgramStatesLoading } = useProgramStates({ programId: program.id });
  const [selectedGrades, setSelectedGrades] = useState<Record<number, string>>({});
  const [initialGrades, setInitialGrades] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!programStates.length) return;

    const initialState = {};

    programStates.forEach((programState) => {
      if (programState.grade && programState.state_id) {
        initialState[programState.state_id] = programState.grade;
      }
    });

    setInitialGrades(initialState);
    setSelectedGrades(initialState);
  }, [programStates]);

  const onSubmit = () => {
    const payload = Object.entries(selectedGrades)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([stateId, grade]) => grade.trim() !== '')
      .map(([stateId, grade]) => ({
        grade,
        state_id: parseInt(stateId, 10),
      }));

    setIsSubmitting(true);
    Axios.post(Routes.plato_api_set_grades_program_program_states_path(program), { payload })
      .then(() => modalManager.close())
      .finally(() => setIsSubmitting(false));
  };

  return (
    <>
      <Button
        className="btn btn--sm btn--outline-purple"
        onClick={modalManager.open}
      >
        <i aria-hidden="true" className="fa fa-pencil-square-o tw-mr-1" />
        Edit Grade Levels by State
      </Button>

      <SubmitModal
        nearlyFullScreen
        headerText="Edit Grades by State"
        isLoading={isStatesLoading || isProgramStatesLoading || isSubmitting}
        isValid={!recordsEqual(initialGrades, selectedGrades)}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        {...modalManager}
      >
        <h2 className="tw-flex tw-justify-between tw-m-0 tw-mb-4 tw-font-bold">
          <span className="tw-m-auto">{program.title}</span>
          <span>National Grade: {program.grade}</span>
        </h2>

        <div className="tw-flex tw-flex-col tw-flex-wrap tw-overflow-auto">
          {states.map(state => (
            <StateGradeSelect
              key={state.id}
              selectedGrades={selectedGrades}
              setSelectedGrades={setSelectedGrades}
              state={state}
            />
          ))}
        </div>
      </SubmitModal>
    </>
  );
};

const EditProgramStateGradeWrapper = (props: EditProgramStateGradeProps) => (
  <QueryClientProvider client={queryClient}>
    <EditProgramStateGrade {...props} />
  </QueryClientProvider>
);

export default EditProgramStateGradeWrapper;
